<template>
  <div
    v-if="isBusy"
    class="row">
    <div class="col-12">
      <h5 data-cy="application-form-section-misc">
        {{ $t('message.applicationForm.miscInfo') }}
      </h5>
    </div>
    <!-- Busy -->
    <div
      class="col text-center">
      <coozzy-spinner />
    </div>
  </div>
  <div
    v-else
    class="row">
    <div class="col-12">
      <h5 data-cy="application-form-section-misc">
        {{ $t('message.applicationForm.miscInfo') }}
      </h5>
    </div>
    <div class="col-12 col-md-6">
      <label for="foreclosure">{{ $t('message.applicationForm.foreclosureCompany') }} {{ (sending ? '*' : '' ) }}</label>
      <coozzy-form-select
        id="foreclosure"
        v-model="foreclosureLastTwoYears"
        data-cy="application-form-company-foreclosure-select"
        :state="object && object.category !== 'PARKING_SPACE' && object.category !== 'SECONDARY_ROOM' && $v.foreclosureLastTwoYears.$dirty && $v.foreclosureLastTwoYears.$error ? false : null">
        <option value="UNDEFINED_VALUE">
          -
        </option>
        <option value="APPLIES">
          {{ $t('message.generic.selectField.yes') }}
        </option>
        <option value="DOES_NOT_APPLY">
          {{ $t('message.generic.selectField.no') }}
        </option>
      </coozzy-form-select>
    </div>
    <div class="col-12 col-md-6">
      <label for="debtCertificate">{{ $t('message.applicationForm.debtCertificateCompany') }} {{ (sending ? '*' : '' ) }}</label>
      <coozzy-form-select
        id="debtCertificate"
        v-model="debtCertificateLastFiveYears"
        data-cy="application-form-company-debt-select"
        :state="object && object.category !== 'PARKING_SPACE' && object.category !== 'SECONDARY_ROOM' && $v.debtCertificateLastFiveYears.$dirty && $v.debtCertificateLastFiveYears.$error ? false : null">
        <option value="UNDEFINED_VALUE">
          -
        </option>
        <option value="APPLIES">
          {{ $t('message.generic.selectField.yes') }}
        </option>
        <option value="DOES_NOT_APPLY">
          {{ $t('message.generic.selectField.no') }}
        </option>
      </coozzy-form-select>
    </div>
    <div class="col-12 col-md-6">
      <label for="liabilityInsurance">{{ $t('message.applicationForm.liabilityInsuranceCompany') }}</label>
      <coozzy-form-select
        id="liabilityInsurance"
        v-model="hasLiabilityInsurance"
        data-cy="application-form-company-liability-insurance-select">
        <option value="UNDEFINED_VALUE">
          -
        </option>
        <option value="APPLIES">
          {{ $t('message.generic.selectField.yes') }}
        </option>
        <option value="DOES_NOT_APPLY">
          {{ $t('message.generic.selectField.no') }}
        </option>
      </coozzy-form-select>
    </div>
    <div
      v-if="parkingOptions.length > 0"
      class="col-12 col-md-12 mt-3">
      <label>
        {{ $t('message.applicationForm.desiredParkingOptions') }}
      </label>
      <div class="row m-0">
        <template v-for="(parkingTypeValue, parkingTypeIndex) in parkingOptions">
          <div
            :key="parkingTypeIndex"
            class="col-12">
            <div class="row">
              <coozzy-form-checkbox
                v-model="parkingTypeValue.checked"
                data-cy="application-form-company-parking-option-checkbox"
                :initial="parkingTypeValue.checked"
                :class="'col-3 d-flex align-items-center'"
                @change="parkingOptionChanged">
                <span>{{ parkingTypeValue.type === 'UNDEFINED_PARKING_TYPE' ? $t('message.onBoarding.buildings.objects.parkingSpaces.title') : $t('message.onBoarding.buildings.objects.parkingSpaces.parkingType.' + parkingTypeValue.type) }}</span><span class="ml-1">(CHF {{ parkingTypeValue.costs }})</span>
              </coozzy-form-checkbox>
              <coozzy-form-input
                v-if="parkingTypeValue.checked"
                :id="'licensePlate_' + parkingTypeIndex"
                v-model="parkingTypeValue.licensePlate"
                data-cy="application-form-company-parking-license-plate-input"
                type="text"
                :class="'col-3'"
                :name="$t('message.applicationForm.licensePlate')"
                @input="parkingOptionChanged" />
            </div>
          </div>
        </template>
      </div>
    </div>
    <div class="col-12 col-md-12 mt-3">
      <coozzy-form-textarea
        ref="remark"
        v-model="remark"
        data-cy="application-form-company-remark-textarea"
        :initial="remark"
        :value-text-area="remark"
        name="remark"
        :rows="2">
        {{ $t('message.applicationForm.remark') }}
      </coozzy-form-textarea>
    </div>
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import { mapMutations } from 'vuex'
import CoozzyFormSelect from '@/framework/components/form/select/CoozzyFormSelect.vue'
import { validation } from '@/mixins/validation'
import { required } from 'vuelidate/lib/validators'
import CoozzySpinner from '@/framework/components/misc/CoozzySpinner.vue'
import * as Sentry from '@sentry/vue'
import CoozzyFormCheckbox from '@/framework/components/form/checkbox/CoozzyFormCheckbox.vue'
import CoozzyFormInput from '@/framework/components/form/input/CoozzyFormInput.vue'
import CoozzyFormTextarea from '@/framework/components/form/textarea/CoozzyFormTextarea.vue'
import { V2RequestedParkingSpace } from '@/types/api'
import { ApplicationFormCompany, ApplicationFormObjectMiscContext } from '@/types/models'

interface ParkingOptionUi {
  type: string
  costs: number
  checked: boolean
  licensePlate: string
}

function isUndefinedValue(value: string) {
  return value !== 'UNDEFINED_VALUE'
}

export default Vue.extend({
  name: 'ApplicationFormCompanyMisc',
  components: {
    CoozzyFormTextarea,
    CoozzyFormInput,
    CoozzyFormCheckbox,
    CoozzyFormSelect,
    CoozzySpinner
  },
  mixins: [validation],
  props: {
    company: {
      type: Object as PropType<ApplicationFormCompany | null>,
      default: null
    },
    sending: {
      type: Boolean,
      default: false
    },
    isBusy: {
      type: Boolean,
      default: false
    },
    object: {
      type: Object as PropType<ApplicationFormObjectMiscContext | null>,
      default: null
    }
  },
  data() {
    return {
      parkingOptions: [] as ParkingOptionUi[]
    }
  },
  computed: {
    foreclosureLastTwoYears: {
      get(): string {
        return this.company?.legal.foreclosureLastTwoYears ?? 'UNDEFINED_VALUE'
      },
      set(value: string) {
        const com = this.company
        if (!com) return
        com.legal.foreclosureLastTwoYears = value
        this.updateCompany(com)
      }
    },
    debtCertificateLastFiveYears: {
      get(): string {
        return this.company?.legal.debtCertificateLastFiveYears ?? 'UNDEFINED_VALUE'
      },
      set(value: string) {
        const com = this.company
        if (!com) return
        com.legal.debtCertificateLastFiveYears = value
        this.updateCompany(com)
      }
    },
    hasLiabilityInsurance: {
      get(): string {
        return this.company?.insurance.hasLiabilityInsurance ?? 'UNDEFINED_VALUE'
      },
      set(value: string) {
        const com = this.company
        if (!com) return
        com.insurance.hasLiabilityInsurance = value
        this.updateCompany(com)
      }
    },
    parkingSpacesRequested: {
      get(): number {
        return this.company?.parkingSpacesRequested ?? 0
      },
      set(value: number) {
        const com = this.company
        if (!com) return
        com.parkingSpacesRequested = value
        this.updateCompany(com)
      }
    },
    remark: {
      get(): string {
        return this.company?.remark ?? ''
      },
      set(value: string) {
        const com = this.company
        if (!com) return
        com.remark = value
        this.updateCompany(com)
      }
    }
  },
  mounted() {
    if (this.object?.parking && this.object.parking.length > 0) {
      this.object.parking.forEach(parking => {
        this.parkingOptions.push({
          type: parking.parkingType,
          costs: parking.costs,
          checked: false,
          licensePlate: ''
        })
      })
    }
  },
  methods: {
    ...mapMutations('applicationForm', ['updateCompany']),
    parkingOptionChanged() {
      const com = this.company
      if (!com) return
      com.requestedParkingSpaces = this.parkingOptions as unknown as V2RequestedParkingSpace[]
      this.updateCompany(com)
    },
    isInvalid() {
      this.$v.$touch()
      if (this.$v.$invalid) {
        Sentry.setExtra('companyMiscValidation', this.$v)
        this.scrollToError()
        return true
      }
      return false
    }
  },
  validations() {
    if (
      this.object &&
      this.object.category !== 'PARKING_SPACE' &&
      this.object.category !== 'SECONDARY_ROOM'
    ) {
      return {
        foreclosureLastTwoYears: {
          required,
          isUndefinedValue
        },
        debtCertificateLastFiveYears: {
          required,
          isUndefinedValue
        }
      }
    }
    return {}
  }
})
</script>

<style lang="scss" scoped>
</style>
